import React, { useState, useEffect } from "react";
import { RefreshCw } from "lucide-react";
// import axios from "axios";
// import { ENV } from "../../utils/env.js";

const formatUptime = (seconds) => {
  if (!seconds || seconds < 0) return "0 hrs 0 mins";
  
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  const parts = [];
  if (days > 0) {
    parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
  }
  if (hours > 0 || days > 0) {
    parts.push(`${hours} ${hours === 1 ? 'hr' : 'hrs'}`);
  }
  if (minutes >= 0) {
    parts.push(`${minutes} ${minutes === 1 ? 'min' : 'mins'}`);
  }
  
  return parts.join(' ');
};

const ServerHealth = () => {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchHealthData = async () => {
    try {
      setLoading(true);
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const dummyMainServerData = {
        version: "2.4.1",
        cpu_percent: 24.7,
        cpu_uptime_hours: 480, 
        cpu_uptime_mins: 30,   
        memory: {
          used_pct: 58
        },
        memory_uptime_hours: 475, 
        memory_uptime_mins: 15,   
        uptime: 98000 
      };
      
      const dummyValidatorData = {
        version: "1.2.0",
        status: "operational"
      };
      
      setHealthData({
        mainServer: dummyMainServerData,
        validator: dummyValidatorData
      });
    } catch (err) {
      console.error("Error fetching health data:", err);
      // Even on error, set some default data to ensure UI works
      setHealthData({
        mainServer: {
          version: "2.4.1",
          cpu_percent: 24.7,
          memory: { used_pct: 58 },
          uptime: 1728000
        },
        validator: { version: "1.2.0", status: "operational" }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchHealthData();
    setRefreshing(false);
  };

  useEffect(() => {
    fetchHealthData();
    // Disable auto-refresh for dummy data
    // const interval = setInterval(fetchHealthData, 30000);
    // return () => clearInterval(interval);
  }, []);

  const StatusBadge = ({ online }) => (
    <span
      className={`px-3 py-1 rounded-full text-sm font-medium ${
        online ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
      }`}
    >
      {online ? "Online" : "Offline"}
    </span>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
          <div className="h-6 w-32 bg-gray-200 rounded animate-pulse" />
          <div className="h-8 w-8 bg-gray-200 rounded-full animate-pulse" />
        </div>

        {/* Content skeletons */}
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex items-center justify-between border-b pb-3">
            <div className="space-y-2">
              <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
              <div className="h-3 w-24 bg-gray-200 rounded animate-pulse" />
            </div>
            <div className="h-6 w-16 bg-gray-200 rounded animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  const mainServer = healthData?.mainServer;
  const validator = healthData?.validator;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">System health</h3>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="p-2 rounded hover:bg-gray-100 transition-colors disabled:opacity-50"
        >
          <RefreshCw
            className={`w-5 h-5 text-gray-700 ${refreshing ? "animate-spin" : ""}`}
          />
        </button>
      </div>

      {/* Main Server */}
      <div className="flex items-center justify-between border-b pb-3">
        <div>
          <p className="font-medium text-gray-900">Main server</p>
          <p className="text-xs text-gray-500">
            Version: {mainServer?.version || healthData?.mainServer?.version || "2.4.1"}
          </p>
        </div>
        <StatusBadge online={!!mainServer || !!healthData?.mainServer} />
      </div>

      {/* Validator */}
      <div className="flex items-center justify-between border-b pb-3">
        <div>
          <p className="font-medium text-gray-900">Validator</p>
          <p className="text-xs text-gray-500">External validation service</p>
        </div>
        <StatusBadge online={!!validator} />
      </div>

      {/* CPU Usage */}
      <div className="flex items-center justify-between border-b pb-3">
        <div>
          <p className="font-medium text-gray-900">CPU usage</p>
          <p className="text-xs text-green-600">
            {mainServer 
              ? `Good • ${mainServer.cpu_uptime_hours || 480} hrs ${mainServer.cpu_uptime_mins || 30} mins`
              : "Good"}
          </p>
        </div>
        <p className="font-bold text-gray-900">
          {mainServer ? `${mainServer.cpu_percent.toFixed(1)}%` : "—"}
        </p>
      </div>

      {/* Memory Usage */}
      <div className="flex items-center justify-between border-b pb-3">
        <div>
          <p className="font-medium text-gray-900">Memory usage</p>
          <p className="text-xs text-green-600">
            {mainServer 
              ? `Good • ${mainServer.memory_uptime_hours || 475} hrs ${mainServer.memory_uptime_mins || 15} mins`
              : "Good"}
          </p>
        </div>
        <p className="font-bold text-gray-900">
          {mainServer ? `${mainServer.memory.used_pct.toFixed(0)}%` : "—"}
        </p>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <p className="font-medium text-gray-900">Uptime</p>
          <p className="text-xs text-gray-500">System running time</p>
        </div>
        <p className="font-bold text-gray-900">
          {mainServer ? formatUptime(mainServer.uptime) : "—"}
        </p>
      </div>
    </div>
  );
};

export default ServerHealth;